import { makeAutoObservable } from 'mobx';

import { PinnedItemsBatchAddCommand } from '@/drive/entities/DriveCommand/PinnedItemsBatchAddCommand';
import { TransientDriveItem } from '@/drive/entities/transientDriveItem';
import { PinnedItem } from '@/forwarding/entities/PinnedItem/PinnedItem';
import { PinnedItemsShareConfig } from '@/forwarding/entities/ShareConfig/PinnedItemsShareConfig';
import { createEmptyForm } from '@/forwarding/types/ForwardForm';
import { ForwardInfo } from '@/forwarding/types/ForwardInfo';
import { getPinnedItemsTitle } from '@/forwarding/utils/getPinnedItemsTitle';

export interface ForwardPinnedItemsConfigOptions {
  title?: string;
  isShareEnabled?: boolean;
  isCollectionDestinationOnly?: boolean;
}

export class ForwardPinnedItemsConfig {
  readonly driveCommand: PinnedItemsBatchAddCommand;

  readonly share?: PinnedItemsShareConfig;

  readonly title?: string;

  readonly isCollectionDestinationOnly: boolean;

  private dtoItems: Nullable<TransientDriveItem[]> = null;

  constructor(
    private readonly pinnedItems: PinnedItem[],
    loadingDtoItems: Promise<Nullable<TransientDriveItem>[]>,
    options: ForwardPinnedItemsConfigOptions = {},
  ) {
    makeAutoObservable<ForwardPinnedItemsConfig, 'pinnedItems'>(this, {
      driveCommand: false,
      share: false,
      title: false,
      isCollectionDestinationOnly: false,
      pinnedItems: false,
    });

    const { title, isShareEnabled = true, isCollectionDestinationOnly = false } = options;

    this.title = title;
    this.isCollectionDestinationOnly = isCollectionDestinationOnly;
    this.driveCommand = new PinnedItemsBatchAddCommand(pinnedItems, loadingDtoItems);

    if (isShareEnabled) {
      this.share = new PinnedItemsShareConfig(
        pinnedItems,
        () => this.info,
        () => this.dtoItems,
      );
    }

    loadingDtoItems.then(this.setDtoItems);
  }

  get info(): ForwardInfo {
    return {
      ...createEmptyForm(),
      preview: { type: 'pinnedItems', items: this.pinnedItems, dtoItems: this.dtoItems },
      title: getPinnedItemsTitle(this.pinnedItems),
    };
  }

  private setDtoItems = (dtoItems: Nullable<TransientDriveItem>[]) => {
    this.dtoItems = dtoItems.filter((item): item is TransientDriveItem => item !== null);
  };
}
